<template>
  <content-dialog
    v-model="dialogOpen"
    fullscreen-tablet
    max-height="85vh"
    card-class="appointment-card"
    without-blur
    :toolbar-classes="{'text-white': true, 'bg-blue-8': !isArchived, 'bg-red-8': isArchived}"
    :width="selectedTab === 'timeline' ? '1100px' : '660px'"
    :min-height="selectedTab === 'timeline' ? '85vh' : '50vh'"
    :tags="[helpCodes.appointments, helpCodes.scheduler]"
    :loading="loading"
    @hide="close"
  >
    <template #toolbar>
      <q-toolbar>
        <q-btn
          flat
          round
          icon="close"
          @click="close"
        />
        <page-toolbar-title class="q-pl-sm">
          <template v-if="isArchived || (!loading && !isEditingAllowed)" #subtitle>
            {{ concatStrIf(' | ', isArchived && 'Archived', !isEditingAllowed && 'Read-only') }}
          </template>
          Appointment
        </page-toolbar-title>
        <q-btn
          v-if="!!eventObject && eventObject.status !== 'UNSCHEDULED' && isEditingAllowed"
          :icon="eventObject.is_locked ? 'mdi-lock' : 'mdi-lock-open'"
          :label="eventObject.is_locked ? 'Locked' : undefined"
          :class="{'opaque-80': eventObject.is_locked}"
          :round="!eventObject.is_locked"
          :rounded="eventObject.is_locked"
          no-caps
          flat
          class="q-mr-sm"
          @click="toggleLock"
        >
          <q-tooltip>
            <span v-if="eventObject.is_locked">Unlock appointment</span>
            <span v-else>Lock editing appointment from calendar</span>
          </q-tooltip>
        </q-btn>
        <help-button />
      </q-toolbar>

      <q-tabs
        v-if="!!eventObject"
        v-model="selectedTab"
        inline-label
        dense
        no-caps
        active-color="white"
        align="left"
        narrow-indicator
      >
        <q-tab
          name="details"
          label="Details"
        />
        <q-tab
          v-if="can(permissions.canAccessCalendar) || can(permissions.canAccessInvoicing) || can(permissions.canAccessReports)"
          name="timeline"
          label="Activity Timeline"
          icon="mdi-chart-gantt"
        />
      </q-tabs>
    </template>

    <div v-if="!!eventObject && !loading">
      <q-tab-panels v-model="selectedTab" class="overflow-panels">
        <q-tab-panel
          name="details"
          class="q-pa-none q-pt-sm"
        >
          <page-banner
            v-if="eventObject.is_locked && isEditingAllowed"
            dense
            align="center"
            class="q-mx-sm q-mb-sm"
            label="This appointment is locked. It cannot be modified directly from the calendar."
          />
          <DetailsPanel
            :without-client="withoutClient"
            :event="eventObject"
            :can-edit="isEditingAllowed"
            :can-perform-simple-actions="canPerformSimpleActions"
            :with-quick-links="withQuickLinks"
            :is-archived="isArchived"
            :with-recurring="withRecurring"
            :timeline="simpleTimeline"
            @go-to-timeline="selectedTab = 'timeline'"
            @open-manual-reminder="$refs.msgDialog.open()"
            @view-staff="viewingStaffID = $event"
            @view-client="viewingClientID = $event"
            @delete-upcoming="deleteUpcomingDialog = true"
            @create-report="addReportWithAppointment"
            @create-invoice="addInvoiceWithAppointment"
            @create-quotation="addQuotationWithAppointment"
          />
        </q-tab-panel>
        <q-tab-panel
          v-if="can(permissions.canAccessCalendar) || can(permissions.canAccessInvoicing) || can(permissions.canAccessReports)"
          name="timeline"
          class="q-pa-none"
        >
          <TimelinePanel
            :id="eventObject.id"
            :can-edit="canPerformSimpleActions"
            :is-recurring="eventObject.is_recurring"
            @add-timeline="(payload) => addTimelineEntry(simpleTimeline, payload)"
            @update-timeline="(payload) => updateTimelineEntry(simpleTimeline, payload)"
            @pin-timeline="(reference, val) => pinTimelineEntry(simpleTimeline, reference, val)"
            @remove-timeline="(payload) => removeTimelineEntry(simpleTimeline, payload)"
          />
        </q-tab-panel>
      </q-tab-panels>
    </div>
    <div v-else-if="!loading">
      <no-results
        icon="mdi-calendar-remove"
        title="Could not load appointment"
      />
    </div>

    <template #bottom>
      <q-toolbar v-if="!!eventObject && (isEditingAllowed || allowDeleting) && selectedTab === 'details'">
        <q-space />
        <q-btn
          v-if="isEditingAllowed && allowDeleting"
          flat
          color="red"
          label="Delete"
          icon-right="mdi-delete"
          @click="$refs.deleteDialog.open()"
        />
        <q-btn
          v-if="isEditingAllowed && allowEditing"
          icon-right="edit"
          label="Edit"
          color="blue-grey"
          @click="$refs.editEventModal.open()"
        />
      </q-toolbar>
    </template>
  </content-dialog>

  <ViewClientModal
    :id="viewingClientID"
    :with-quick-links="false"
    @close="viewingClientID = null"
    @updated="getAppointment(true)"
  />
  <ViewStaffModal
    :id="viewingStaffID"
    allow-editing
    allow-archiving
    @close="viewingStaffID = null"
    @updated="getAppointment(true)"
  />

  <DeleteConfirmationDialog
    ref="deleteDialog"
    :recurring="eventObject && eventObject.is_recurring"
    @delete="deleteAppointment"
  />

  <message-dialog
    v-model="deleteUpcomingDialog"
    title="Confirm"
  >
    <q-item-label>Are you sure you want to delete all upcoming appointments after this appointment's start date?</q-item-label>

    <template #after>
      <page-banner
        dense
        dark
        color="red-7"
        class="q-mx-sm"
        content-classes="text-bold"
        label="This action cannot be undone. Any reports, clients and assigned staff members will no longer be accessible under the deleted appointments."
      />
    </template>

    <template #actions>
      <q-btn
        v-close-popup
        flat
        label="Cancel"
      />
      <q-btn
        v-close-popup
        flat
        autofocus
        label="Continue"
        color="red"
        @click="deleteUpcomingAppointments"
      />
    </template>
  </message-dialog>

  <EditEventModal
    v-if="isEditingAllowed && eventObject"
    ref="editEventModal"
    :event="eventObject"
    :without-client="withoutClient"
    @updated="onEventUpdated"
  />

  <ClientMessageDialog
    v-if="eventObject"
    :id="eventObject.id"
    ref="msgDialog"
    :clients="eventObject.clients"
    :status="eventObject.status"
  />
</template>

<script>
import { defineAsyncComponent } from 'vue'
import { concatStrIf } from 'src/helpers/globals'

import contextMenuMixin from '../mixins/contextMenuMixin'
import statusMixin from '../mixins/statusMixin'
import eventioMixin from '../mixins/io/eventReceiverMixin'
import eventSenderMixin from '../mixins/io/eventSenderMixin'
import calendarSenderMixin from '../mixins/io/calendarSenderMixin'
import timelineMergerMixin from 'components/partials/timeline/partials/timelineMergerMixin'

import EditEventModal from '../../Edit'
import ViewStaffModal from '../../../staff/partials/view/Dialog'
import DeleteConfirmationDialog from '../fullcalendar/DeleteConfirmationDialog'
import ClientMessageDialog from '../ClientMessageDialog'

import TimelinePanel from './panels/Timeline'
import DetailsPanel from './panels/Details'

export default {
  components: {
    EditEventModal,
    ViewStaffModal,
    DeleteConfirmationDialog,
    ClientMessageDialog,
    TimelinePanel,
    DetailsPanel,
    ViewClientModal: defineAsyncComponent(() => import('../../../clients/partials/view/Dialog'))
  },
  mixins: [contextMenuMixin, statusMixin, eventioMixin, eventSenderMixin, calendarSenderMixin, timelineMergerMixin],
  props: {
    allowEditing: {
      type: Boolean,
      default: false
    },
    allowDeleting: {
      type: Boolean,
      default: false
    },
    id: {
      type: Number,
      default: null
    },
    withoutClient: {
      type: Boolean,
      default: false
    },
    withQuickLinks: {
      type: Boolean,
      default: true
    },
    withRecurring: {
      type: Boolean,
      default: true
    },
    // bind ?back=true into URL after performing quick action (create report, inv, quote etc)
    goBackAfterQuickAction: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close', 'deleted', 'updated'],
  data () {
    return {
      loading: false,
      eventObject: null,
      simpleTimeline: [],
      dialogOpen: false,
      timer: null,
      viewingClientID: null,
      viewingJobID: null,
      viewingStaffID: null,
      selectedTab: 'details',
      deleteUpcomingDialog: false
    }
  },
  computed: {
    isEditingAllowed () {
      return this.allowEditing && !this.isArchived && (!!this.eventObject && this.eventObject.can_edit)
    },
    canPerformSimpleActions () {
      return !this.isArchived && this.allowEditing && !!this.eventObject && this.can(this.permissions.canAccessCalendar) && (this.eventObject.can_edit || this.eventObject.attendees.map(item => item.id).includes(this.userData.id))
    },
    isArchived () {
      return this.eventObject && this.eventObject.archived
    }
  },
  watch: {
    id () {
      this.getAppointment()
    }
  },
  methods: {
    concatStrIf,
    close () {
      this.ioDisconnect()
      this.dialogOpen = false
      this.eventObject = null
      this.simpleTimeline = []
      this.selectedTab = 'details'
      this.$emit('close')
    },
    async getAppointment (silent = false) {
      if (!silent) {
        this.dialogOpen = false
        this.eventObject = null
        this.simpleTimeline = []
      }

      if (this.id === null) { return }

      this.dialogOpen = true
      if (!silent) {
        this.loading = true
      }
      this.ioListen()
      const { payload, status } = await this.$get('v1/appointments/find', {
        id: parseInt(this.id)
      })
      this.loading = false
      if (status === 'success') {
        this.eventObject = payload.appointment
        this.simpleTimeline = payload.timeline
      }
    },
    addReportWithAppointment () {
      if (this.eventObject.clients.length < 1) {
        this.$q.notify({
          color: 'red',
          message: 'Appointment must have at least one client in order to create a new report'
        })
        return
      }

      this.goTo('reports/add', {
        appointment_id: this.eventObject.id,
        back: this.goBackAfterQuickAction || undefined
      })
    },
    addQuotationWithAppointment () {
      if (this.eventObject.clients.length < 1) {
        this.$q.notify({
          color: 'red',
          message: 'Appointment must have at least one client in order to create a new quotation'
        })
        return
      }
      this.goTo('quotations/add', {
        appointment_id: this.eventObject.id,
        back: this.goBackAfterQuickAction || undefined
      })
    },
    addInvoiceWithAppointment () {
      if (this.eventObject.clients.length < 1) {
        this.$q.notify({
          color: 'red',
          message: 'Appointment must have at least one client in order to create a new invoice'
        })
        return
      }

      this.goTo('invoicing/add', {
        appointment_id: this.eventObject.id,
        back: this.goBackAfterQuickAction || undefined
      })
    },
    deleteAppointment (deleteAction = 'none') {
      this.baseDeleteAppointment(this.eventObject.id, deleteAction, () => {
        this.$emit('deleted')
        this.dialogOpen = false
      })
    },
    onEventUpdated (payload) {
      this.getAppointment()
      this.$emit('updated', payload)
    },
    async toggleLock () {
      this.eventObject.is_locked = !this.eventObject.is_locked
      const { status } = await this.$put('v1/appointments/lock', {
        id: this.eventObject.id,
        state: this.eventObject.is_locked
      })
      if (status === 'success') {
        const sendPayload = {
          id: this.eventObject.id,
          state: this.eventObject.is_locked
        }

        this.$bus.emit('calendar:lock', sendPayload)
        this.ioCalendarLock(sendPayload)
        this.ioEventLock(sendPayload)
      }
    },
    async deleteUpcomingAppointments () {
      this.$loading.show()
      const { status, payload } = await this.$remove('v1/appointments/upcoming', {
        id: this.eventObject.id
      })
      this.$loading.hide()
      if (status === 'success') {
        this.$q.notify({
          message: 'Upcoming appointments have successfully been deleted',
          color: 'orange-6',
          textColor: 'black'
        })
        this.$bus.emit('calendar:remove', payload.deleted_ids)
        this.ioCalendarRemove(payload.deleted_ids)
        for (const dId of payload.deleted_ids) {
          const ele = this.eventObject.recurring_appointments.map(e => e.id).indexOf(dId)
          if (ele > -1) {
            this.eventObject.recurring_appointments.splice(ele, 1)
          }
        }
      }
    }
  },
  mounted () {
    this.getAppointment()
  }
}
</script>
